import type { FetchBaseQueryMeta, QueryReturnValue } from '@reduxjs/toolkit/query/react';
import { z } from 'zod';
import { config } from '../config';
import { getSingleOrThrow } from '../utils';
import {
    type Asset,
    AssetStatus,
    type Association,
    type Device,
    assetSchema,
    assetsResponseSchema,
    associationsResponseSchema,
    associationsWithEmbeddedDevicesResponseSchema,
    devicesResponseSchema,
} from '../features/api/assetAdministrationBackendCodec';
import { type ApiError, baseApi } from './baseApi';

const RIO_AUDIT_LOG_HEADER = 'x-rio-audit-reason';
const ASSET_ADMIN_FETCH_LIMIT = Number.parseInt(config.assetsAdministrationFetchLimit ?? '1000', 10);

export const DEFAULT_VEHICLE_BRAND = 'MAN';
export const VIN_PATTERN = /^[0-9ABCDEFGHJKLMNPRSTUVWXYZ]{17}$/;

type AssetAdministrationApiError = {
    status: number | string;
    message: string;
    data?: unknown;
};

type ApiQueryResult<T> = QueryReturnValue<T, AssetAdministrationApiError, FetchBaseQueryMeta>;

const errorMessageForStatus = (status: number | string): string => {
    if (status === 401) {
        return 'UNAUTHENTICATED';
    }
    if (status === 403) {
        return 'ACCESS_DENIED';
    }
    if (status === 404) {
        return 'NOT_FOUND';
    }
    return 'Backend error';
};

const transformAssetAdministrationError = (response: ApiError): AssetAdministrationApiError => ({
    status: response.status,
    message: errorMessageForStatus(response.status),
    data: response.data,
});

const parseResponse = <T>(
    response: QueryReturnValue<unknown, ApiError, FetchBaseQueryMeta>,
    schema: z.ZodType<T>
): ApiQueryResult<T> => {
    if (response.error) {
        return { error: transformAssetAdministrationError(response.error), meta: response.meta };
    }

    const parsed = schema.safeParse(response.data);
    if (!parsed.success) {
        return {
            error: {
                status: 'PARSING_ERROR',
                message: `Invalid payload: ${parsed.error.message}`,
            },
            meta: response.meta,
        };
    }

    return { data: parsed.data, meta: response.meta };
};

const acceptExpectedStatus = (
    response: QueryReturnValue<unknown, ApiError, FetchBaseQueryMeta>,
    expectedStatus: number
): ApiQueryResult<void> => {
    if (response.error) {
        return { error: transformAssetAdministrationError(response.error), meta: response.meta };
    }

    if (response.meta?.response?.status !== expectedStatus) {
        return {
            error: {
                status: response.meta?.response?.status ?? 'UNKNOWN_ERROR',
                message: 'UNEXPECTED_STATUS_CODE',
            },
            meta: response.meta,
        };
    }

    return { data: undefined, meta: response.meta };
};

const assetUrl = (path: string): string => `${config.backend.ASSET_ADMINISTRATION_SERVICE}${path}`;

export const assetsApi = baseApi.injectEndpoints({
    endpoints: builder => ({
        getAssetsByVin: builder.query<Asset[], { vin: string }>({
            query: ({ vin }) => ({
                url: assetUrl('/assets'),
                params: {
                    identification: vin,
                    identification_type: 'vin',
                    embed: '(master_data)',
                },
            }),
            transformResponse: response => assetsResponseSchema.parse(response).items,
            transformErrorResponse: transformAssetAdministrationError,
            providesTags: result =>
                result ? result.map(asset => ({ type: 'Asset' as const, id: asset.id })) : ['Asset'],
        }),
        getAssetsByVinNoEmbed: builder.query<Asset[], { vin: string }>({
            query: ({ vin }) => ({
                url: assetUrl('/assets'),
                params: {
                    identification: vin,
                    identification_type: 'vin',
                },
            }),
            transformResponse: response => assetsResponseSchema.parse(response).items,
            transformErrorResponse: transformAssetAdministrationError,
            providesTags: result =>
                result ? result.map(asset => ({ type: 'Asset' as const, id: asset.id })) : ['Asset'],
        }),
        getAssetsByAccountId: builder.query<Asset[], string>({
            async queryFn(accountId, _api, _extraOptions, runBaseQuery) {
                const assets: Asset[] = [];
                let nextUrl: string | undefined = assetUrl('/assets');
                let params: Record<string, string> | undefined = {
                    account_id: accountId,
                    limit: ASSET_ADMIN_FETCH_LIMIT.toString(),
                    embed: '(master_data)',
                };

                while (nextUrl) {
                    const response = await runBaseQuery({ url: nextUrl, params });
                    const parsed = parseResponse(response, assetsResponseSchema);
                    if (parsed.error) {
                        return { error: parsed.error, meta: parsed.meta };
                    }
                    assets.push(...parsed.data.items);
                    nextUrl = parsed.data._links?.next?.href;
                    params = undefined;
                }

                return { data: assets };
            },
            providesTags: result =>
                result ? result.map(asset => ({ type: 'Asset' as const, id: asset.id })) : ['Asset'],
        }),
        getAsset: builder.query<Asset, string>({
            query: assetId => ({
                url: assetUrl(`/assets/${encodeURIComponent(assetId)}`),
                params: { embed: '(master_data)' },
            }),
            transformResponse: response => assetSchema.parse(response),
            transformErrorResponse: transformAssetAdministrationError,
            providesTags: (_result, _error, assetId) => [{ type: 'Asset', id: assetId }],
        }),
        getDevicesForAsset: builder.query<Device[], { assetId: string }>({
            query: ({ assetId }) => ({
                url: assetUrl('/associations'),
                params: { asset_id: assetId, embed: '(device)' },
            }),
            transformResponse: response => {
                const parsed = associationsWithEmbeddedDevicesResponseSchema.parse(response);
                return parsed.items.flatMap(association =>
                    association._embedded?.device ? [association._embedded.device] : []
                );
            },
            transformErrorResponse: transformAssetAdministrationError,
            providesTags: (_result, _error, { assetId }) => [{ type: 'Asset', id: assetId }],
        }),
        getDevicesForAccount: builder.query<{ device: Device; assetId: string }[], string>({
            async queryFn(accountId, _api, _extraOptions, runBaseQuery) {
                const devices: { device: Device; assetId: string }[] = [];
                let nextUrl: string | undefined = assetUrl('/associations');
                let params: Record<string, string> | undefined = {
                    account_id: accountId,
                    embed: '(device)',
                    limit: ASSET_ADMIN_FETCH_LIMIT.toString(),
                };

                while (nextUrl) {
                    const response = await runBaseQuery({ url: nextUrl, params });
                    const parsed = parseResponse(response, associationsWithEmbeddedDevicesResponseSchema);
                    if (parsed.error) {
                        return { error: parsed.error, meta: parsed.meta };
                    }
                    devices.push(
                        ...parsed.data.items.flatMap(association =>
                            association._embedded?.device
                                ? [{ device: association._embedded.device, assetId: association.asset_id }]
                                : []
                        )
                    );
                    nextUrl = parsed.data._links?.next?.href;
                    params = undefined;
                }

                return { data: devices };
            },
        }),
        getDeviceByIdentification: builder.query<Device[], { identification: string; deviceType: string }>({
            query: ({ identification, deviceType }) => ({
                url: assetUrl('/devices'),
                params: { identification, device_type: deviceType },
            }),
            transformResponse: response => devicesResponseSchema.parse(response).items,
            transformErrorResponse: transformAssetAdministrationError,
        }),
        getAssociationsByDeviceId: builder.query<Association[], string>({
            query: deviceId => ({
                url: assetUrl('/associations'),
                params: { device_id: deviceId },
            }),
            transformResponse: response => associationsResponseSchema.parse(response).items,
            transformErrorResponse: transformAssetAdministrationError,
        }),
        deleteAsset: builder.mutation<void, { assetId: string; reason: string }>({
            async queryFn({ assetId, reason }, _api, _extraOptions, runBaseQuery) {
                const response = await runBaseQuery({
                    url: assetUrl(`/assets/${encodeURIComponent(assetId)}`),
                    method: 'DELETE',
                    headers: { [RIO_AUDIT_LOG_HEADER]: reason },
                });
                return acceptExpectedStatus(response, 202);
            },
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'Asset', id: assetId }],
        }),
        archiveAsset: builder.mutation<void, { assetId: string; reason: string }>({
            async queryFn({ assetId, reason }, _api, _extraOptions, runBaseQuery) {
                const assetResponse = parseResponse(
                    await runBaseQuery({ url: assetUrl(`/assets/${encodeURIComponent(assetId)}`) }),
                    assetSchema
                );
                if (assetResponse.error) {
                    return { error: assetResponse.error, meta: assetResponse.meta };
                }
                const response = await runBaseQuery({
                    url: assetUrl(`/assets/${encodeURIComponent(assetId)}`),
                    method: 'PUT',
                    headers: { [RIO_AUDIT_LOG_HEADER]: reason },
                    body: { ...assetResponse.data, status: AssetStatus.ARCHIVED },
                });
                return acceptExpectedStatus(response, 202);
            },
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'Asset', id: assetId }],
        }),
        activateAsset: builder.mutation<void, { assetId: string; reason: string }>({
            async queryFn({ assetId, reason }, _api, _extraOptions, runBaseQuery) {
                const assetResponse = parseResponse(
                    await runBaseQuery({ url: assetUrl(`/assets/${encodeURIComponent(assetId)}`) }),
                    assetSchema
                );
                if (assetResponse.error) {
                    return { error: assetResponse.error, meta: assetResponse.meta };
                }
                const response = await runBaseQuery({
                    url: assetUrl(`/assets/${encodeURIComponent(assetId)}`),
                    method: 'PUT',
                    headers: { [RIO_AUDIT_LOG_HEADER]: reason },
                    body: { ...assetResponse.data, status: AssetStatus.ACTIVE },
                });
                return acceptExpectedStatus(response, 202);
            },
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'Asset', id: assetId }],
        }),
        associateDeviceToAsset: builder.mutation<
            void,
            { assetId: string; deviceIdentification: string; deviceType: string; reason: string }
        >({
            async queryFn({ assetId, deviceIdentification, deviceType, reason }, _api, _extraOptions, runBaseQuery) {
                const devicesResponse = parseResponse(
                    await runBaseQuery({
                        url: assetUrl('/devices'),
                        params: { identification: deviceIdentification, device_type: deviceType },
                    }),
                    devicesResponseSchema
                );
                if (devicesResponse.error) {
                    return { error: devicesResponse.error, meta: devicesResponse.meta };
                }

                let device: Device;
                try {
                    device = getSingleOrThrow(
                        devicesResponse.data.items,
                        devicesCount => `Exactly one device must match, but found ${devicesCount.toString()}`
                    );
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            message: error instanceof Error ? error.message : String(error),
                        },
                    };
                }

                const association: Association = {
                    id: crypto.randomUUID(),
                    asset_id: assetId,
                    device_id: device.id,
                };
                const response = await runBaseQuery({
                    url: assetUrl(`/associations/${association.id}`),
                    method: 'PUT',
                    headers: { [RIO_AUDIT_LOG_HEADER]: reason },
                    body: association,
                });
                return acceptExpectedStatus(response, 202);
            },
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'Asset', id: assetId }],
        }),
        disassociateDeviceFromAsset: builder.mutation<void, { assetId: string; deviceId: string; reason: string }>({
            async queryFn({ assetId, deviceId, reason }, _api, _extraOptions, runBaseQuery) {
                const associationsResponse = parseResponse(
                    await runBaseQuery({ url: assetUrl('/associations'), params: { device_id: deviceId } }),
                    associationsResponseSchema
                );
                if (associationsResponse.error) {
                    return { error: associationsResponse.error, meta: associationsResponse.meta };
                }

                let association: Association;
                try {
                    association = getSingleOrThrow(
                        associationsResponse.data.items.filter(
                            item => item.asset_id === assetId && item.device_id === deviceId
                        ),
                        associationsCount =>
                            `Exactly one association must match, but found ${associationsCount.toString()}`
                    );
                } catch (error) {
                    return {
                        error: {
                            status: 'CUSTOM_ERROR',
                            message: error instanceof Error ? error.message : String(error),
                        },
                    };
                }

                const response = await runBaseQuery({
                    url: assetUrl(`/associations/${association.id}`),
                    method: 'DELETE',
                    headers: { [RIO_AUDIT_LOG_HEADER]: reason },
                });
                return acceptExpectedStatus(response, 202);
            },
            invalidatesTags: (_result, _error, { assetId }) => [{ type: 'Asset', id: assetId }],
        }),
        getBrandByVin: builder.query<string, string>({
            query: vin => ({ url: assetUrl(`/vins/${encodeURIComponent(vin)}`) }),
            transformResponse: response => {
                const parsed = z.object({ brand: z.string().trim().min(1) }).parse(response);
                return parsed.brand;
            },
            transformErrorResponse: transformAssetAdministrationError,
        }),

        createAsset: builder.mutation<void, { asset: Asset; auditReason?: string }>({
            query: ({ asset, auditReason }) => {
                return {
                    url: assetUrl(`/assets/${asset.id}`),
                    method: 'PUT',
                    headers: auditReason ? { [RIO_AUDIT_LOG_HEADER]: auditReason } : undefined,
                    body: asset,
                };
            },
            transformErrorResponse: transformAssetAdministrationError,
        }),

        createAssociation: builder.mutation<Association, { assetId: string; deviceId: string; auditReason?: string }>({
            query: ({ assetId, deviceId, auditReason }) => {
                const associationId = `${crypto.randomUUID()}`;
                return {
                    url: assetUrl(`/associations/${associationId}`),
                    method: 'PUT',
                    headers: auditReason ? { [RIO_AUDIT_LOG_HEADER]: auditReason } : undefined,
                    body: {
                        id: associationId,
                        device_id: deviceId,
                        asset_id: assetId,
                    },
                };
            },
            transformErrorResponse: transformAssetAdministrationError,
        }),
    }),
});

export const {
    useGetAssetsByVinQuery,
    useLazyGetAssetsByVinQuery,
    useLazyGetAssetsByVinNoEmbedQuery,
    useGetAssetsByAccountIdQuery,
    useLazyGetAssetsByAccountIdQuery,
    useGetAssetQuery,
    useLazyGetAssetQuery,
    useGetDevicesForAssetQuery,
    useLazyGetDevicesForAssetQuery,
    useGetDevicesForAccountQuery,
    useLazyGetDevicesForAccountQuery,
    useGetDeviceByIdentificationQuery,
    useGetAssociationsByDeviceIdQuery,
    useDeleteAssetMutation,
    useArchiveAssetMutation,
    useActivateAssetMutation,
    useAssociateDeviceToAssetMutation,
    useDisassociateDeviceFromAssetMutation,
    useLazyGetBrandByVinQuery,
    useCreateAssetMutation,
    useCreateAssociationMutation,
} = assetsApi;
